import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RecyclingRoundedIcon from "@mui/icons-material/RecyclingRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const INK = "#071313";
const WHITE = "#FFFFFF";
const MUTED = "rgba(7, 19, 19, 0.68)";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";


function EcoRoundedIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M19.9 3.1C13.4 3.4 8.5 5.4 5.7 8.9C3.4 11.8 3.2 15.3 4.1 17.7L2.7 19.1C2.3 19.5 2.3 20.1 2.7 20.5C3.1 20.9 3.7 20.9 4.1 20.5L5.5 19.1C7.9 20.2 11.4 19.9 14.3 17.6C17.8 14.8 19.8 9.9 20.1 3.4C20.1 3.2 20 3.1 19.9 3.1ZM7.3 17.3C8.1 14.2 10.4 11.2 14.2 8.4C14.7 8.1 15.3 8.2 15.6 8.7C15.9 9.2 15.8 9.8 15.3 10.1C11.9 12.6 9.9 15.2 9.2 17.8C8.5 17.8 7.9 17.6 7.3 17.3Z" />
    </SvgIcon>
  );
}

const sustainabilityText = `
Environmental Sustainability

Last updated: 11 June 2026

Art For My Room is a digital art discovery platform. Our purpose is to help people choose art more thoughtfully, support artists more directly, and reduce unnecessary friction in the way art is discovered, previewed and purchased.

We are still a growing platform, but environmental responsibility is part of the way we want to build. This commitment explains how Art For My Room aims to reduce waste, encourage considered buying and support more sustainable choices where possible.

1. Art chosen with more confidence

One of the quiet problems in home decor is uncertainty. People buy items online, discover they do not suit the room, and then return them, replace them or leave them unused.

Art For My Room starts with the actual room. Buyers upload a room photo and discover paintings, prints, sculptures and decorative objects that better match the space, colours, scale and mood.

By helping buyers make more confident decisions, we aim to reduce:

- unsuitable purchases
- unnecessary returns
- repeated deliveries
- decorative waste
- impulse buying that does not last
- art and decor that remains unused

A well-chosen artwork is more likely to be kept, loved and lived with.

2. Supporting local and independent artists

Where possible, Art For My Room wants to support local and independent artists. Local discovery can reduce unnecessary long-distance shipping and make the buying experience more personal and practical.

When buyers can discover artists closer to them, it may help:

- reduce transport distance
- simplify delivery
- support local creative economies
- create stronger relationships between buyers and artists
- give artists visibility without relying only on large global marketplaces

We do not claim that every sale will be local, but we want locality to become part of the discovery experience.

3. Digital-first discovery

Art For My Room is built around digital discovery. Buyers can explore artwork pages, artist pages and room previews before deciding what to buy.

This helps reduce the need for:

- repeated physical samples
- unnecessary showroom travel
- printed catalogues
- speculative purchases
- avoidable returns

A digital-first journey allows buyers to compare, preview and think before moving towards a purchase.

4. Matching before buying

The room matching feature is not only a convenience. It is also a more considered way to buy decor.

Instead of asking buyers to imagine whether something might work, Art For My Room lets them preview selected art in relation to their own room. This creates a pause between desire and purchase.

We believe that better visual context can support slower, more intentional buying.

5. Responsible print and decorative object choices

Prints and decorative objects are more affordable than original artworks, but affordability should not encourage careless consumption.

As Art For My Room develops, we want our in-house products to be chosen carefully and presented clearly. Our aim is to avoid unnecessary clutter and focus on items that genuinely improve a room.

For prints and decorative objects, we aim to consider:

- useful sizes
- quality materials
- durable presentation
- responsible packaging where possible
- simple product ranges rather than endless overproduction
- clear information so buyers understand what they are purchasing

6. Reducing unnecessary packaging and delivery waste

Art and decor often require protective packaging. This is necessary, especially for original paintings, sculptures and fragile objects. However, packaging should be thoughtful, not excessive.

As the platform grows, we aim to encourage:

- protective but sensible packaging
- reuse of packaging where appropriate
- recyclable or lower-impact packaging where practical
- careful delivery planning
- clear delivery information before purchase
- fewer avoidable returns through better product and room matching

We want artworks to arrive safely without treating packaging as an afterthought.

7. Long-life objects over fast decor

Art For My Room is built around the idea that art should belong in a space, not simply fill a gap.

Original paintings and sculptures often carry emotional, cultural and personal value. They are not disposable decor. They can move with a person from home to home and gain meaning over time.

Our long-term aim is to encourage buyers to choose fewer, better, more meaningful objects.

8. Helping artists make more from existing work

Many artists already have paintings and sculptures available. Our platform helps these works become more visible before more new material needs to be produced.

By improving discovery for existing artworks, Art For My Room can help reduce the pressure to constantly create new inventory just to be seen online.

Better presentation, clearer descriptions, SEO-friendly artwork pages and room matching can give existing works a better chance of finding the right buyer.

9. Technology with a lighter purpose

AI tools consume resources, and digital platforms have an environmental footprint. We do not pretend technology is impact-free.

Our aim is to use AI where it creates practical value:

- helping buyers make better decisions
- helping artists present work more effectively
- reducing unnecessary browsing friction
- improving matching between artworks and rooms
- supporting useful discovery rather than endless consumption

We want technology to make the buying journey more thoughtful, not more wasteful.

10. Continuous improvement

Art For My Room is still developing. Our sustainability work will improve as the platform grows and we learn more from artists, buyers and partners.

Future improvements may include:

- clearer delivery impact information
- local artist discovery filters
- packaging guidance for artists
- sustainable shipping recommendations
- better product lifecycle information
- highlighting durable and long-lasting works
- encouraging repair, reuse and careful ownership

11. Our commitment

We want Art For My Room to be a platform where beauty and responsibility can sit at the same table.

Our environmental commitment is to support more thoughtful discovery, reduce unnecessary waste where possible, encourage long-life creative objects and build a platform that helps people choose art with more care.

12. Contact

For questions about our environmental sustainability approach, contact:

Art For My Room
Email: info@artformyroom.com
`;

const sustainabilityCards = [
  {
    icon: <EcoRoundedIcon />,
    title: "Thoughtful buying",
    text: "Room matching helps buyers choose art with more confidence before purchase.",
  },
  {
    icon: <LocalShippingRoundedIcon />,
    title: "Smarter delivery",
    text: "Clearer choices and better matching can reduce unsuitable purchases and avoidable returns.",
  },
  {
    icon: <PaletteRoundedIcon />,
    title: "Long-life art",
    text: "Original paintings and sculptures are meaningful objects, not fast decor.",
  },
  {
    icon: <RecyclingRoundedIcon />,
    title: "Less waste",
    text: "We aim to support sensible packaging, digital discovery and more considered product choices.",
  },
];

export default function EnvironmentalSustainabilityPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#FBFFFE",
        color: INK,
        fontFamily: BODY_FONT,
        background: `
          radial-gradient(circle at 8% 8%, ${alpha(TURQUOISE, 0.15)}, transparent 30%),
          radial-gradient(circle at 90% 12%, ${alpha(GOLD, 0.18)}, transparent 28%),
          radial-gradient(circle at 86% 82%, ${alpha(BURGUNDY, 0.11)}, transparent 34%),
          linear-gradient(180deg, #ffffff 0%, #f6fffd 50%, #fff8e8 100%)
        `,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{
            mb: 3,
            color: TURQUOISE_DARK,
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Back home
        </Button>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 80px rgba(7, 19, 19, 0.10)",
            bgcolor: WHITE,
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              color: WHITE,
              background: `
                radial-gradient(circle at 12% 10%, ${alpha(TURQUOISE, 0.36)}, transparent 32%),
                radial-gradient(circle at 88% 15%, ${alpha(GOLD, 0.26)}, transparent 30%),
                linear-gradient(135deg, ${TURQUOISE_DARK}, ${BURGUNDY_DARK})
              `,
            }}
          >
            <Stack spacing={2}>
              <Chip
                icon={<EcoRoundedIcon />}
                label="Commitments"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: alpha(WHITE, 0.16),
                  color: WHITE,
                  fontWeight: 900,
                  border: `1px solid ${alpha(WHITE, 0.22)}`,
                  "& .MuiChip-icon": { color: WHITE },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: "2.35rem", md: "4.35rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.075em",
                  lineHeight: 0.95,
                  maxWidth: 980,
                }}
              >
                Environmental Sustainability
              </Typography>

              <Typography
                sx={{
                  color: alpha(WHITE, 0.84),
                  lineHeight: 1.85,
                  maxWidth: 820,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                How Art For My Room aims to support more thoughtful art buying,
                reduce avoidable waste and encourage long-life creative objects.
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={2.4} sx={{ mb: 4 }}>
              {sustainabilityCards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2.4,
                      borderRadius: 5,
                      border: `1px solid ${LINE}`,
                      bgcolor: "#FBFFFE",
                    }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 4,
                        display: "grid",
                        placeItems: "center",
                        mb: 1.5,
                        color: WHITE,
                        background: `linear-gradient(135deg, ${TURQUOISE}, ${GOLD}, ${BURGUNDY})`,
                      }}
                    >
                      {card.icon}
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 950,
                        fontFamily: HEADING_FONT,
                        fontSize: "1.2rem",
                        letterSpacing: "-0.035em",
                        mb: 0.8,
                      }}
                    >
                      {card.title}
                    </Typography>

                    <Typography sx={{ color: MUTED, lineHeight: 1.65 }}>
                      {card.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 4, borderColor: LINE }} />

            <Typography
              component="article"
              sx={{
                whiteSpace: "pre-line",
                color: MUTED,
                lineHeight: 1.85,
                fontSize: "1rem",
                maxWidth: 850,
                mx: "auto",
                "&::first-line": {
                  color: INK,
                  fontWeight: 950,
                  fontSize: "1.4rem",
                },
              }}
            >
              {sustainabilityText}
            </Typography>

            <Divider sx={{ my: 4, borderColor: LINE }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
            >
              <Button
                component={RouterLink}
                to="/artist-room-matcher"
                variant="contained"
                startIcon={<PublicRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: TURQUOISE_DARK,
                  "&:hover": { bgcolor: TURQUOISE },
                }}
              >
                Match art with my room
              </Button>

              <Button
                component={RouterLink}
                to="/artist-launch"
                variant="outlined"
                startIcon={<StorefrontRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: BURGUNDY,
                  borderColor: alpha(BURGUNDY, 0.35),
                }}
              >
                Upload original art
              </Button>

              <Button
                component={RouterLink}
                to="/commitments/social-impact"
                variant="outlined"
                startIcon={<VolunteerActivismRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: GOLD_DARK,
                  borderColor: alpha(GOLD, 0.45),
                }}
              >
                Social impact
              </Button>

              <Button
                component={RouterLink}
                to="/artist-painting-analyzer"
                variant="outlined"
                startIcon={<LightbulbRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: TURQUOISE_DARK,
                  borderColor: alpha(TURQUOISE, 0.4),
                }}
              >
                Artist tools
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
